// ============================================
// I18N TRANSLATIONS INDEX
// ============================================

// Auto-import all translation files (*.ts except index.ts)
const translationModules = import.meta.glob<{ [key: string]: any }>('./*.ts', { eager: true });

// Build translations object dynamically from file names
// Example: './es.ts' -> { es: {...} }
export const translations: Record<string, any> = Object.entries(translationModules).reduce(
    (acc, [path, module]) => {
        // Extract language code from file path: './es.ts' -> 'es'
        const langCode = path.replace('./', '').replace('.ts', '');

        // Skip index.ts (this file)
        if (langCode === 'index') return acc;

        // Get the exported translation object (es, en, fr, etc.)
        const translationData = module[langCode];

        if (translationData) {
            acc[langCode] = translationData;
        }

        return acc;
    },
    {} as Record<string, any>
);

/**
 * Get nested value from object using dot notation path
 * @param obj - Object to navigate
 * @param path - Dot notation path (e.g., 'form.fail')
 * @returns Value at path or undefined
 */
function getNestedValue(obj: any, path: string): string | undefined {
    const keys = path.split('.');
    let current = obj;

    for (const key of keys) {
        if (current && typeof current === 'object' && key in current) {
            current = current[key];
        } else {
            return undefined;
        }
    }

    return typeof current === 'string' ? current : undefined;
}

/**
 * Get translation for a key in the specified language
 * Supports dot notation for nested keys (e.g., 'form.fail')
 *
 * @param key - Translation key (supports dot notation like 'form.fail')
 * @param lang - Language code (es, en, etc.)
 * @param fallback - Optional fallback text if key not found
 * @returns Translated string
 */
export function t(key: string, lang: string, fallback?: string): string {
    const translation = getNestedValue(translations[lang], key);
    return translation || fallback || key;
}
